import type { Asset, AssetHistory, Coordinates, LocationFix, Place, Rule, Schedule, Shipment, Stop, Timestamp, Trip, WorkspaceData } from './types.ts';
export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const MAX_OBSERVATION_GAP = 90 * MINUTE;
export function distanceM(a: Coordinates, b: Coordinates): number {
  const rad = Math.PI / 180;
  const p = Math.sin((b.lat - a.lat) * rad / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin((b.lng - a.lng) * rad / 2) ** 2;
  return 12_742_000 * Math.atan2(Math.sqrt(p), Math.sqrt(Math.max(0, 1 - p)));
}
export function contains(place: Place, point: Coordinates): boolean {
  const g = place.geometry;
  if (g.type === 'circle') return distanceM(g.center, point) <= g.radiusM;
  let inside = false;
  for (let i = 0, j = g.vertices.length - 1; i < g.vertices.length; j = i++) {
    const a = g.vertices[i], b = g.vertices[j];
    if ((a.lat > point.lat) !== (b.lat > point.lat) && point.lng < (b.lng - a.lng) * (point.lat - a.lat) / (b.lat - a.lat) + a.lng) inside = !inside;
  }
  return inside;
}
export function placeAt(places: readonly Place[], fix: Coordinates): Place | undefined {
  return places.find(p => contains(p, fix));
}
/** Derive only observed visits and trips. A long gap terminates dwell evidence. */
export function deriveHistory(fixes: readonly LocationFix[], places: readonly Place[]): Pick<AssetHistory, 'stops' | 'trips'> {
  const stops: Stop[] = [], trips: Trip[] = [];
  let stop: Stop | undefined;
  let trip: Trip | undefined;
  let previous: LocationFix | undefined;
  let previousPlace: string | null = null;
  for (const fix of fixes) {
    const placeId = placeAt(places, fix)?.id ?? null;
    const gap = !!previous && fix.seenAt - previous.seenAt > MAX_OBSERVATION_GAP;
    if (stop && (placeId !== stop.placeId || gap)) {
      stop.leftAt = gap ? null : fix.seenAt;
      stop.endedBy = gap ? 'gap' : 'exit';
      stops.push(stop); stop = undefined;
    }
    if (placeId && !stop) {
      stop = { id: `stop:${fix.id}`, workspaceId: fix.workspaceId, assetId: fix.assetId, placeId,
        arrivedAt: fix.seenAt, leftAt: null, lastSeenAt: fix.seenAt, firstFixId: fix.id, lastFixId: fix.id, observedDwellMs: 0, endedBy: null };
    } else if (stop) {
      stop.lastFixId = fix.id; stop.lastSeenAt = fix.seenAt; stop.observedDwellMs = fix.seenAt - stop.arrivedAt;
    }
    if (previous && (placeId === null || placeId !== previousPlace)) {
      trip ??= { id: `trip:${previous.id}`, workspaceId: fix.workspaceId, assetId: fix.assetId,
        startedAt: previous.seenAt, endedAt: null, originPlaceId: previousPlace, destinationPlaceId: null,
        firstFixId: previous.id, lastFixId: previous.id, observedDistanceM: 0, hasGaps: false };
      trip.lastFixId = fix.id; trip.hasGaps ||= gap;
      // Across a coverage gap, even a straight-line segment is not an observed distance.
      if (!gap) trip.observedDistanceM += distanceM(previous, fix);
    }
    if (trip && placeId) {
      trip.endedAt = fix.seenAt; trip.destinationPlaceId = placeId;
      trips.push(trip); trip = undefined;
    }
    previous = fix; previousPlace = placeId;
  }
  if (stop) stops.push(stop);
  if (trip) trips.push(trip);
  return { stops, trips };
}
export function ruleMatchesAsset(rule: Rule, asset: Asset, data: Pick<WorkspaceData, 'shipments'>): boolean {
  switch (rule.scope.kind) {
    case 'workspace': return true;
    case 'assets': return rule.scope.assetIds.includes(asset.id);
    case 'group': return asset.groupId === rule.scope.groupId;
    case 'shipment': {
      const shipmentId = rule.scope.shipmentId;
      return data.shipments.find(s => s.id === shipmentId)?.assetIds.includes(asset.id) ?? false;
    }
  }
}
const formatters = new Map<string, Intl.DateTimeFormat>();
/** End is exclusive. Overnight windows belong to the day on which they start. */
export function inSchedule(schedule: Schedule | null, at: Timestamp): boolean {
  if (!schedule) return true;
  let fmt = formatters.get(schedule.timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone: schedule.timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    formatters.set(schedule.timeZone, fmt);
  }
  const parts = Object.fromEntries(fmt.formatToParts(at).map(p => [p.type, p.value]));
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
  const minute = Number(parts.hour) * 60 + Number(parts.minute);
  if (schedule.startMinute === schedule.endMinute) return schedule.days.includes(day);
  if (schedule.startMinute < schedule.endMinute) return schedule.days.includes(day) && minute >= schedule.startMinute && minute < schedule.endMinute;
  return (minute >= schedule.startMinute && schedule.days.includes(day)) || (minute < schedule.endMinute && schedule.days.includes((day + 6) % 7));
}
export interface RuleOccurrence { at: Timestamp; detectedAt: Timestamp; evidenceFixId: string; placeId: string | null }
/** Pure, replayable evaluation. Idempotency key is rule + asset + evidence + time. */
export function evaluateRule(rule: Rule, history: AssetHistory, places: readonly Place[], now: Timestamp): RuleOccurrence[] {
  if (!rule.enabled) return [];
  const place = places.find(p => p.id === rule.placeId);
  const fixes = history.fixes;
  if (rule.type === 'not_seen') {
    const last = fixes.at(-1);
    const threshold = rule.thresholdMs;
    return last && threshold !== null && now - last.seenAt >= threshold
      ? [{ at: last.seenAt + threshold, detectedAt: Math.max(last.receivedAt, last.seenAt + threshold), evidenceFixId: last.id, placeId: placeAt(places, last)?.id ?? null }] : [];
  }
  if (!place) return [];
  if (rule.type === 'dwell') return history.stops.filter(s => s.placeId === place.id && s.observedDwellMs >= (rule.thresholdMs ?? Infinity)).map(s => {
    const fix = fixes.find(f => f.seenAt >= s.arrivedAt + (rule.thresholdMs ?? 0) && f.seenAt <= s.lastSeenAt)!;
    return { at: fix.seenAt, detectedAt: fix.receivedAt, evidenceFixId: fix.id, placeId: place.id };
  });
  const events: RuleOccurrence[] = [];
  for (let i = 1; i < fixes.length; i++) {
    const before = fixes[i - 1], after = fixes[i];
    const was = contains(place, before), inside = contains(place, after);
    if (rule.type === 'perimeter_exit' ? was && !inside : !was && inside) {
      events.push({ at: after.seenAt, detectedAt: after.receivedAt, evidenceFixId: after.id, placeId: place.id });
    }
  }
  return events.filter(e => e.at >= rule.createdAt);
}
/** Historical lane ETA, only from at least three completed, same-lane observations. */
export function deriveShipmentEta(shipment: Shipment, shipments: readonly Shipment[]): number | null {
  if (shipment.status !== 'in_transit' || shipment.departedAt === null) return null;
  const departedAt = shipment.departedAt;
  const durations = shipments.filter(s => s.id !== shipment.id && s.status === 'delivered'
    && s.originPlaceId === shipment.originPlaceId && s.destinationPlaceId === shipment.destinationPlaceId
    && s.departedAt !== null && s.deliveredAt !== null && s.deliveredAt <= departedAt)
    .map(s => s.deliveredAt! - s.departedAt!).filter(n => n > 0).sort((a, b) => a - b);
  if (durations.length < 3) return null;
  const mid = Math.floor(durations.length / 2);
  const median = durations.length % 2 ? durations[mid] : (durations[mid - 1] + durations[mid]) / 2;
  return departedAt + median;
}
